import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ThemeProvider,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { createAppTheme } from '@/app/theme';
import { useThemePreference } from '@/app/themePreference';
import {
  TABLE_THEME_OPTIONS,
  useTableTheme,
  type TableThemePreset,
} from '@/app/tableTheme';

interface TableThemeDialogProps {
  open: boolean;
  onClose: () => void;
}

export function TableThemeDialog({ open, onClose }: TableThemeDialogProps) {
  const { preset, savePreset, isSaving } = useTableTheme();
  const { resolvedTheme } = useThemePreference();
  const [draft, setDraft] = useState<TableThemePreset>(preset);
  const previewTheme = useMemo(
    () => createAppTheme(resolvedTheme, draft),
    [draft, resolvedTheme],
  );

  useEffect(() => {
    if (open) setDraft(preset);
  }, [open, preset]);

  const save = async () => {
    await savePreset(draft);
    onClose();
  };

  return (
    <Dialog open={open} onClose={isSaving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Table appearance</DialogTitle>
      <DialogContent>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Choose how data tables look for every user. Light and dark mode remain automatic.
        </Typography>

        <Box
          role="radiogroup"
          aria-label="Table theme"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
            gap: 1.5,
          }}
        >
          {TABLE_THEME_OPTIONS.map((option) => {
            const selected = option.value === draft;
            return (
              <Paper
                key={option.value}
                component="button"
                type="button"
                role="radio"
                aria-checked={selected}
                variant="outlined"
                onClick={() => setDraft(option.value)}
                sx={{
                  minHeight: 92,
                  p: 2,
                  borderWidth: selected ? 2 : 1,
                  borderColor: selected ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'action.selected' : 'background.paper',
                  color: 'text.primary',
                  cursor: 'pointer',
                  textAlign: 'left',
                  font: 'inherit',
                  transition: 'border-color 150ms ease, background-color 150ms ease',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography fontWeight={700}>{option.label}</Typography>
                  {selected && <CheckCircleIcon color="primary" aria-hidden="true" />}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {option.description}
                </Typography>
              </Paper>
            );
          })}
        </Box>

        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 3, mb: 1 }}>
          Live preview
        </Typography>
        <ThemeProvider theme={previewTheme}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="Table theme preview">
              <TableHead>
                <TableRow>
                  <TableCell>Receipt</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow hover>
                  <TableCell>GRN-2026-00421</TableCell>
                  <TableCell>MH Global</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell align="right">1,240</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell>GRN-2026-00422</TableCell>
                  <TableCell>North Distribution</TableCell>
                  <TableCell>Processing</TableCell>
                  <TableCell align="right">875</TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell>GRN-2026-00423</TableCell>
                  <TableCell>Central Retail</TableCell>
                  <TableCell>Pending</TableCell>
                  <TableCell align="right">360</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </ThemeProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={isSaving || draft === preset}>
          {isSaving ? 'Saving…' : 'Apply to everyone'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
